/**
 * Client-side PostHog event wrapper.
 *
 * The PostHog counterpart to `lib/analytics.ts` (which talks to GA4). Some
 * funnel events — most notably `upgrade_clicked` — historically fired ONLY to
 * GA4 via `trackEvent`, so PostHog never saw them and the free-first-job
 * activation funnel (see `lib/marketing/free-job-pack.ts`) could not be built
 * end-to-end in PostHog. Call this alongside `trackEvent` at those call sites so
 * the same interaction lands in both destinations.
 *
 * Why a plain function rather than `usePostHog()`: several upgrade CTAs
 * (`pricing-cta.tsx`, the `document-detail-view` watermark toast) fire from
 * imperative handlers that don't already hold the hook. A shared function that
 * reads the same `posthog-js` singleton the provider initialises keeps a single
 * consistent, consent-gated code path across every surface.
 *
 * Consent + readiness. Mirrors `trackEvent`: no-ops on SSR, when cookie consent
 * isn't `accepted`, or before the SDK singleton has loaded. `posthog.capture`'s
 * own `before_send` barrier fails closed too, but gating here avoids relying on
 * it and matches the GA4 wrapper's shape.
 */

import posthog from "posthog-js";
import { readAnalyticsConsent } from "@/components/analytics/browser-attribution";
import { POSTHOG_BROWSER_INITIALIZED_EVENT } from "@/lib/analytics/posthog-browser-lifecycle";
import { subscribeToBrowserConsent } from "@/components/analytics/browser-consent-lifecycle";

/**
 * Run a MOUNT-TIME capture once the browser SDK can actually send it.
 *
 * posthog-js drops (does not queue) a capture made before `init`. Marketing
 * surfaces initialise a returning visitor's SDK at idle instead of inside the
 * hydration commit (PostHogProvider `deferInitialInit`, draft 8 §2.2 A2), so a
 * capture fired from a mount effect or an early IntersectionObserver callback
 * would be lost. This runs `callback` now when the SDK has loaded; when
 * Analytics consent is ALREADY accepted but the SDK is still initialising it
 * waits for POSTHOG_BROWSER_INITIALIZED_EVENT; otherwise (no consent) it does
 * nothing, exactly as the capture would have. It never turns pre-consent
 * activity into a later capture.
 *
 * Self-cleaning, so fire-and-forget callers need not hold the cancel: a held
 * capture is dropped the moment any consent update leaves Analytics not
 * accepted, if the URL (path, query or fragment) has changed by the time the
 * SDK is ready (the SDK would stamp it with the wrong `$current_url`), or
 * after a bounded wait. `callback` keeps its own consent guard, and
 * `before_send` remains the final barrier. Returns a cancel for effect
 * cleanups.
 */
const MAX_HOLD_MS = 30_000;

export function whenPostHogReady(callback: () => void): () => void {
  if (typeof window === "undefined") return () => {};
  if (posthog.__loaded) {
    callback();
    return () => {};
  }
  if (readAnalyticsConsent() !== "accepted") return () => {};
  const url = window.location.href;
  let settled = false;
  let unsubscribeConsent: () => void = () => {};
  const release = () => {
    if (settled) return;
    settled = true;
    window.removeEventListener(POSTHOG_BROWSER_INITIALIZED_EVENT, onReady);
    window.clearTimeout(holdTimer);
    unsubscribeConsent();
  };
  function onReady() {
    const sameUrl = window.location.href === url;
    release();
    if (sameUrl && readAnalyticsConsent() === "accepted") callback();
  }
  window.addEventListener(POSTHOG_BROWSER_INITIALIZED_EVENT, onReady);
  const holdTimer = window.setTimeout(release, MAX_HOLD_MS);
  try {
    unsubscribeConsent = subscribeToBrowserConsent(() => {
      if (readAnalyticsConsent() !== "accepted") release();
    });
  } catch {
    // A capture helper must never throw into the click handler that called
    // it. Without a way to watch for a withdrawal, drop the held capture.
    release();
    return release;
  }
  if (settled) unsubscribeConsent();
  return release;
}

type EventPropertyValue = string | number | boolean | null | undefined;

export type PostHogEventProperties = Record<string, EventPropertyValue>;

/**
 * Fire a PostHog custom event from the browser. Silently no-ops on SSR, without
 * accepted consent, or before the SDK has loaded. Safe to call unconditionally
 * from any client component or event handler.
 */
export function capturePostHogEvent(
  name: string,
  properties: PostHogEventProperties = {},
): void {
  if (typeof window === "undefined") return;
  if (readAnalyticsConsent() !== "accepted") return;

  // Strip undefined so PostHog doesn't record the literal string "undefined";
  // null is meaningful and kept (matches the GA4 wrapper).
  const cleaned: Record<string, Exclude<EventPropertyValue, undefined>> = {};
  for (const [key, value] of Object.entries(properties)) {
    if (value !== undefined) cleaned[key] = value;
  }

  // Consent is accepted here, so a not-yet-loaded SDK is the marketing
  // surfaces' idle init (see whenPostHogReady): send once it is ready.
  whenPostHogReady(() => {
    if (!posthog.__loaded || readAnalyticsConsent() !== "accepted") return;
    posthog.capture(name, cleaned);
  });
}
